/**
 * Contextual breadcrumb trail.
 *
 * The server renders the canonical crumbs (walking taxonomy.parent); this script makes
 * the VISIBLE trail reflect the full path the visitor navigated, using the
 * #from=<venue>&zone=<zone> context that tree/card links carry.
 *
 * The context rides in the URL FRAGMENT: it is read only here and in
 * collection-browse.js, never by the server, so a fragment carries it just as well
 * without minting a crawlable URL per venue/zone pair. See
 * theme/docs/plans/gsc-coverage-2026-07.md.
 *
 * It used to assume the canonical chain NEVER contained a venue or zone below a zone
 * page, and unconditionally prepended both. That held only while group→zone parents
 * were unset — 63 collections had no taxonomy.parent at all, so a leaf breadcrumbed as
 * `Home > Group > Leaf` and the two missing levels came from here. Once those parents
 * exist the canonical chain supplies Venue and Zone itself, and prepending would print
 * each of them twice.
 *
 * So nothing is assumed about the shape now: the rendered crumbs are classified against
 * the venue and zone maps by href, and only what is genuinely absent gets inserted.
 *
 *  - Venue crumb present  → rewrite it to the #from venue (zones are shared across
 *    venues, so the canonical one is often not the one the visitor came through).
 *  - Venue crumb absent   → insert it after Home.
 *  - Zone crumb present   → keep it, carry #from on its link.
 *  - Zone crumb absent    → insert it after the venue crumb.
 *  - Remaining canonical crumbs (the group) get the context appended so navigating up
 *    keeps the trail.
 *
 * JSON-LD is unaffected (it stays canonical).
 */
(function () {
  var nav = document.querySelector('.breadcrumbs');
  if (!nav) return;

  var params = new URLSearchParams(location.hash.slice(1));
  var from = params.get('from');
  var zone = params.get('zone');

  function readMap(sel) {
    var el = document.querySelector(sel);
    if (!el) return {};
    try { return JSON.parse(el.textContent); } catch (e) { return {}; }
  }
  var venueMap = readMap('[data-bq-venue-map]');
  var zoneMap = readMap('[data-bq-zone-map]');

  // href → handle, so a rendered crumb can be recognised without trusting its position
  // in the chain. Compare on pathname only: canonical links carry no fragment, but the
  // ones this script has already touched do.
  function pathOf(u) {
    if (!u) return '';
    var s = String(u).split('#')[0].split('?')[0];
    return s.replace(/\/$/, '');
  }
  function invert(map) {
    var out = {};
    for (var k in map) if (Object.prototype.hasOwnProperty.call(map, k)) out[pathOf(map[k].u)] = k;
    return out;
  }
  var venueByPath = invert(venueMap);
  var zoneByPath = invert(zoneMap);

  function makeSep() {
    var s = document.createElement('span');
    s.className = 'breadcrumbs__sep';
    s.setAttribute('aria-hidden', 'true');
    s.innerHTML = '&gt;';
    return s;
  }
  function makeLink(title, href) {
    var a = document.createElement('a');
    a.className = 'breadcrumbs__link';
    a.href = href;
    a.textContent = title;
    return a;
  }
  // A crumb and the separator that precedes it are one unit — removing or inserting
  // either alone leaves a stray ">".
  function insertPair(link, before) {
    var frag = document.createDocumentFragment();
    frag.appendChild(makeSep());
    frag.appendChild(link);
    nav.insertBefore(frag, before);
    return link;
  }

  var home = nav.querySelector('[data-bq-home]') || nav.querySelector('.breadcrumbs__link');
  if (!home) return;

  // Classify what the server already rendered.
  var venueCrumb = null;
  var zoneCrumb = null;
  var rest = [];
  var crumbs = nav.querySelectorAll('[data-bq-venue-crumb], [data-bq-canon-crumb]');
  for (var i = 0; i < crumbs.length; i++) {
    var a = crumbs[i];
    var p = pathOf(a.getAttribute('href'));
    if (!venueCrumb && venueByPath[p]) venueCrumb = a;
    else if (!zoneCrumb && zoneByPath[p]) zoneCrumb = a;
    else rest.push(a);
  }

  if (!from && !zone) {
    // No context to apply. The canonical trail is already correct and complete.
    return;
  }

  // Venue: rewrite in place, or insert after Home.
  if (venueCrumb) {
    if (from && venueMap[from]) {
      venueCrumb.textContent = venueMap[from].t;
      venueCrumb.href = venueMap[from].u;
    }
  } else if (from && venueMap[from]) {
    venueCrumb = insertPair(makeLink(venueMap[from].t, venueMap[from].u), home.nextSibling);
  }

  // Zone: keep the canonical one (only its context changes), or insert it right after
  // the venue crumb — which is `home` itself when there is no venue. Never insert a
  // crumb pointing at the page being viewed: a zone page reached with its own handle in
  // the context would otherwise get a link to itself next to the current-page crumb.
  var here = pathOf(location.pathname);
  var zoneOk = zone && zoneMap[zone] && pathOf(zoneMap[zone].u) !== here;
  var zoneHref = zoneOk ? zoneMap[zone].u.split('#')[0] + '#from=' + encodeURIComponent(from || '') : null;
  if (zoneCrumb) {
    // Rewrite the label too, not just the link. The canonical zone is whichever venue owns
    // the leaf first in display order, so a visitor arriving from Cafe > Coffee Bar would
    // otherwise read "Bar & Service" — the right venue above a zone they never visited.
    if (zoneHref) {
      zoneCrumb.textContent = zoneMap[zone].t;
      zoneCrumb.setAttribute('href', zoneHref);
    }
  } else if (zoneHref) {
    var after = venueCrumb || home;
    insertPair(makeLink(zoneMap[zone].t, zoneHref), after.nextSibling);
  }

  // Keep context on the crumbs below the zone (e.g. the group) so navigating up holds it.
  var ctx = [];
  if (from) ctx.push('from=' + encodeURIComponent(from));
  if (zone) ctx.push('zone=' + encodeURIComponent(zone));
  if (ctx.length) {
    for (var j = 0; j < rest.length; j++) {
      var href = rest[j].getAttribute('href');
      if (!href || href.indexOf('#') !== -1) continue;
      rest[j].setAttribute('href', href + '#' + ctx.join('&'));
    }
  }
})();
